import classNames from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { CAR_PURCHASE_STATUS } from 'src/constants';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IconCloseDoors } from 'src/general/Icons/IconCloseDoors';
import { IconExpand } from 'src/general/Icons/IconExpand';
import { IconExterior } from 'src/general/Icons/IconExterior';
import { IconInterior } from 'src/general/Icons/IconInterior';
import { IconOpenDoors } from 'src/general/Icons/IconOpenDoors';
import { AddToWatchList, AddToWatchListStyle } from 'src/general/components/AddToWatchList/AddToWatchList';
import { Button } from 'src/general/components/Button/Button';
import { ButtonIcon } from 'src/general/components/Button/ButtonIcon';
import { imageLoaderCarma } from 'src/general/helpers/carmaGenericImageLoader';
import { getMicrocopyBulk } from 'src/stores/microcopyStore';
import { ProductListDataRow } from 'src/types/CataloguePage.types';
import { ProductDetailsPayloadImages } from 'src/types/ProductDetail.type';

import dynamic from 'next/dynamic';
import { useFeatureHotspotsStore } from 'src/stores/featureHotspotsStore';
import { DataLayerPdpEvent, pushToDataLayerPDP } from 'src/utils/pushToDataLayer';
import { CarRotator } from '../CarRotator/CarRotator';
import styles from './CarHero.module.scss';
import { useStatusPillState } from 'src/plp/components/StatusPill/useStatusPillState';

const Interior = dynamic(() => import('src/pdp/components/Interior/Interior').then((mod) => mod.Interior), {
  ssr: false,
});

interface Props {
  sku: string;
  product: Partial<ProductListDataRow>;
  images: ProductDetailsPayloadImages;
  statusPill: ReactNode;
  promoPill: ReactNode;
  hasPromo?: boolean;
  onExpand: () => void;
  carState: CAR_PURCHASE_STATUS;
  onFeatureHotspotClick?: (featureName: string, section: 'exterior' | 'interior') => void;
  onChangeDisplayMode?: (mode: 'doorsOpen' | 'doorsClosed' | 'interior') => void;
}

type DisplayMode = 'doorsOpen' | 'doorsClosed' | 'interior';

export const CarHero: FC<Props> = ({
  sku,
  product,
  images,
  statusPill,
  promoPill,
  carState,
  hasPromo,
  onExpand,
  onFeatureHotspotClick,
  onChangeDisplayMode,
}) => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('doorsClosed');
  const { viewType } = useFeatureHotspotsStore();
  const { status } = useStatusPillState({ sku, state: carState });
  const showClose360Doors = useCallback(() => {
    const option = displayMode === 'interior' ? 'EXTERIOR' : 'CLOSE_DOOR';
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku,
      option,
    });
    setDisplayMode('doorsClosed');
    onChangeDisplayMode?.('doorsClosed');
  }, [displayMode]);

  const showOpen360Doors = useCallback(() => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku,
      option: 'OPEN_DOOR',
    });
    setDisplayMode('doorsOpen');
    onChangeDisplayMode?.('doorsOpen');
  }, []);
  const showInterior = useCallback(() => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku,
      option: 'INTERIOR',
    });
    setDisplayMode('interior');
    onChangeDisplayMode?.('interior');
  }, []);

  useEffect(() => {
    let displayMode: DisplayMode;
    switch (viewType) {
      case 'interior':
        displayMode = 'interior';
        break;
      case 'open':
        displayMode = 'doorsOpen';
        break;
      default:
        displayMode = 'doorsClosed';
        break;
    }
    setDisplayMode(displayMode);
    onChangeDisplayMode?.(displayMode);
  }, [viewType]);

  const { openDoorsLabel, closeDoorsLabel, interiorLabel, exteriorLabel } = getMicrocopyBulk({
    openDoorsLabel: [MICROCOPY.PDP_CAR_HERO_BUTTON_OPEN_DOORS],
    closeDoorsLabel: [MICROCOPY.PDP_CAR_HERO_BUTTON_CLOSE_DOORS],
    interiorLabel: [MICROCOPY.PDP_GALLERY_BUTTON_INTERIOR],
    exteriorLabel: [MICROCOPY.PDP_GALLERY_BUTTON_EXTERIOR],
  });

  return (
    <section className={styles.root}>
      <>
        <div className={styles.playerWrapper}>
          <div
            className={classNames(styles.interiorWrapper, {
              [styles.active]: displayMode === 'interior',
            })}
          >
            <Interior
              image={imageLoaderCarma({ src: images.interior[0].url, width: 4096 })}
              hotspots={product.hotspots?.interior ?? []}
              onClickHotspot={(featureName) => onFeatureHotspotClick?.(featureName, 'interior')}
            />
          </div>
        </div>
        {(displayMode === 'doorsClosed' || displayMode === 'doorsOpen') && (
          <CarRotator
            visible
            imagesData={images}
            displayMode={displayMode}
            hotspots={displayMode === 'doorsClosed' ? (product.hotspots?.exterior ?? []) : []}
            onClickHotspot={(featureName, _) => {
              onFeatureHotspotClick?.(featureName, 'exterior');
            }}
            hasPromo={hasPromo}
            hasStatus={!!status}
          />
        )}
      </>
      <aside className={styles.controls}>
        <ButtonIcon
          className={classNames(styles.controlsButton, styles.controlsButtonExpand)}
          onClick={onExpand}
          variant="tertiary"
          size="small"
          title="Expand"
          Icon={IconExpand}
          data-testid="button-expand"
        />
        {displayMode === 'doorsClosed' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonDesktop)}
              onClick={showOpen360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconOpenDoors />}
              data-testid="button-doorsOpen-desktop"
            >
              {openDoorsLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonMobile)}
              onClick={showOpen360Doors}
              variant="tertiary"
              size="small"
              title="Open doors"
              Icon={IconOpenDoors}
              data-testid="button-doorsOpen-mobile"
            />
          </>
        )}
        {displayMode === 'doorsOpen' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonDesktop)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconCloseDoors />}
              data-testid="button-doorsClosed-desktop"
            >
              {closeDoorsLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonMobile)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              title="Open doors"
              Icon={IconCloseDoors}
              data-testid="button-doorsClosed-mobile"
            />
          </>
        )}
        {(displayMode === 'doorsOpen' || displayMode === 'doorsClosed') && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonDesktop)}
              onClick={showInterior}
              variant="tertiary"
              size="small"
              startIcon={<IconInterior />}
              data-testid="button-interior-desktop"
            >
              {interiorLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonMobile)}
              onClick={showInterior}
              variant="tertiary"
              size="small"
              title="Interior"
              Icon={IconInterior}
              data-testid="button-interior-mobile"
            />
          </>
        )}
        {displayMode === 'interior' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonDesktop)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconExterior />}
              data-testid="button-exterior-desktop"
            >
              {exteriorLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonMobile)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              title="Exterior"
              Icon={IconExterior}
              data-testid="button-exterior-mobile"
            />
          </>
        )}

        <AddToWatchList
          sku={sku}
          product={product}
          style={AddToWatchListStyle.icon}
          className={classNames(styles.controlsButton, styles.controlsButtonWatchlist)}
        />
        <div className={styles.statusWrapper}>{statusPill}</div>
        {carState === 'available' ? <div className={styles.promoPill}>{promoPill}</div> : null}
      </aside>
    </section>
  );
};
